import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../../context/ThemeContext';
import { Card } from '../Card';
import { Button, Input, ThemedText } from '..';
import userService from '../../api/services/userService';

const MIN_PASSWORD_LENGTH = 8;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${props => props.theme.spacing.lg};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.md};
`;

const ChangePasswordCard = () => {
  const theme = useTheme();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setError(null);

    if (!currentPassword) {
      setError('Please enter your current password.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError('New password must include at least one uppercase letter and one number.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setSaving(true);
    try {
      await userService.changePassword({ currentPassword, newPassword });
      toast.success('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err?.message || 'Failed to change password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card theme={theme} style={{ marginBottom: theme.spacing.lg }}>
      <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.xs }}>
        Change Password
      </ThemedText>
      <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.lg }}>
        Update the password you use to sign in to the portal.
      </ThemedText>

      <Input
        label="Current Password"
        type="password"
        autoComplete="current-password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        disabled={saving}
      />

      <FieldGrid theme={theme}>
        <Input
          label="New Password"
          type="password"
          autoComplete="new-password"
          placeholder={`${MIN_PASSWORD_LENGTH}+ chars, 1 uppercase, 1 number`}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={saving}
        />
        <Input
          label="Confirm New Password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={saving}
        />
      </FieldGrid>

      {error && (
        <ThemedText style={{ color: theme.colors.danger, marginTop: theme.spacing.sm }}>
          {error}
        </ThemedText>
      )}

      <Actions theme={theme}>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : 'Update Password'}
        </Button>
      </Actions>
    </Card>
  );
};

export default ChangePasswordCard;
